<div class="page-wrap" [class.is-embedded]="embedded">
  <div class="pinned" *ngIf="!embedded">
    <button nbButton size="small" ghost type="button" (click)="back()">
      <nb-icon icon="arrow-back-outline"></nb-icon> {{ 'crm.opportunities' | translate }}
    </button>
  </div>

  <div *ngIf="errorMessage" class="error-banner">{{ errorMessage }}</div>

  <ng-container *ngIf="opportunity as opp">
    <div class="opp-header">
      <div class="title-row">
        <h1 *ngIf="!embedded">{{ opp.title }}</h1>
        <input *ngIf="embedded" nbInput fullWidth type="text"
               class="edit-title-input"
               [(ngModel)]="editTitle"
               (blur)="saveGeneral()"
               [disabled]="savingGeneral"
               [placeholder]="'crm.title_placeholder' | translate">
      </div>

      <div class="opp-meta">
        <span class="status-badge" [class]="'is-' + opp.status">{{ ('crm.opp.' + opp.status) | translate }}</span>
        <a class="meta-item vacancy-link inline-flex" *ngIf="opp.MarketVacancy_id"
           [routerLink]="['/market/vacancies', opp.MarketVacancy_id]"
           (click)="closeIfEmbedded($event)">
          {{ 'crm.view_full_vacancy' | translate }}
        </a>
        <!-- closeIfEmbedded: these navigate the page BEHIND the dialog, so in
             dialog mode the destination would open with the dialog still
             covering it. routerLink still does the navigating. -->
        <a class="meta-item meta-link" *ngIf="opp.end_client?.name"
           [routerLink]="['/clients', opp.end_client?.id]"
           (click)="closeIfEmbedded($event)">{{ 'crm.create.end_client' | translate }}: {{ opp.end_client?.name }}</a>
        <a class="meta-item meta-link" *ngIf="opp.broker?.name"
           [routerLink]="['/clients', opp.via_Client_id]"
           (click)="closeIfEmbedded($event)">{{ 'crm.broker_label' | translate }}: {{ opp.broker?.name }}</a>
        <span class="meta-item">{{ 'crm.source_label' | translate }}: {{ opp.source }}</span>
      </div>

      <textarea *ngIf="embedded && !opp.MarketVacancy_id"
                nbInput fullWidth
                class="edit-desc-input"
                [(ngModel)]="editDescription"
                (blur)="saveGeneral()"
                [disabled]="savingGeneral"
                [placeholder]="'crm.desc_placeholder' | translate"></textarea>
      <!-- Under the header, as in submission-detail: collapsed by default, so a long scraped
           description costs a few lines of the first viewport. Inside .opp-header so its
           pre-wrap keeps an intake mail's paragraphs. -->
      <section class="opp-description-block"
               *ngIf="descriptionOf(opp) && (!embedded || opp.MarketVacancy_id)">
        <header class="desc-head">
          <span class="desc-label">{{ 'description' | translate }}</span>
          <button nbButton ghost size="tiny" type="button"
                  [attr.aria-expanded]="descExpanded"
                  (click)="descExpanded = !descExpanded">
            {{ (descExpanded ? 'showless' : 'showmore') | translate }}
          </button>
        </header>
        <p class="opp-description" [class.is-collapsed]="!descExpanded">{{ descriptionOf(opp) }}</p>
      </section>
    </div>

    <!-- Two-column layout: activity feed (main) + sidebar (status + submissions) -->
    <div class="detail-layout">

      <!-- Sidebar: status dropdown + submissions list -->
      <aside class="detail-rail" *ngIf="canCreate">
        <section class="rail-block">
          <div class="rail-heading">
            <h2 class="section-title"><nb-icon icon="info-outline"></nb-icon>{{ 'crm.details' | translate }}</h2>
            <span class="status-badge" [class]="'is-' + opp.status">{{ ('crm.opp.' + opp.status) | translate }}</span>
          </div>
          <!-- Won is derived; show it as a disabled sentinel so the value is visible -->
          <div class="status-row">
            <nb-select [(selected)]="selectedStatus"
                       [disabled]="saving || opp.status === 'won'"
                       (selectedChange)="changeStatus($event)"
                       size="small"
                       [placeholder]="'status' | translate">
              <nb-option *ngFor="let s of selectableStatuses" [value]="s">
                <span class="opt-chip" [class]="'chip-' + s">{{ ('crm.opp.' + s) | translate }}</span>
              </nb-option>
              <!-- Won sentinel — users cannot pick it -->
              <nb-option [value]="'won'" [disabled]="true" *ngIf="opp.status === 'won'">
                <span class="opt-chip chip-won">{{ 'crm.status_won' | translate }} <small>{{ 'crm.status_won_hint' | translate }}</small></span>
              </nb-option>
            </nb-select>
            <!-- Named here, not just on the disabled won option: without this the
                 relationship to the submissions list below is invisible until a
                 submission has already overridden the opportunity's status. -->
            <span class="status-hint" *ngIf="submissions.length">{{ 'crm.status_derived_hint' | translate }}</span>
          </div>

          <!-- Rate / location / date fields. start+end drive the deal's value
               (rate x duration); without them the pipeline cannot be valued. -->
          <div class="rate-fields">
            <label class="rate-field">
              <span class="rate-label">{{ 'crm.rate_indication' | translate }}</span>
              <input nbInput currencyMask [options]="Globals.currency"
                     class="rate-input"
                     [(ngModel)]="editRateTarget"
                     (blur)="saveValueRate()"
                     [disabled]="savingRates"
                     placeholder="—">
            </label>
            <label class="rate-field">
              <span class="rate-label">{{ 'crm.hours_per_week' | translate }}</span>
              <input nbInput type="number"
                     class="rate-input"
                     min="1" [max]="hoursMax" step="any"
                     [(ngModel)]="editHours"
                     (blur)="saveValueRate()"
                     [disabled]="savingRates"
                     placeholder="—">
              <span class="rate-hint" *ngIf="rawHours">{{ 'crm.hours_stated' | translate: { value: rawHours } }}</span>
            </label>
            <label class="rate-field">
              <span class="rate-label">{{ 'crm.location' | translate }}</span>
              <input nbInput type="text"
                     class="rate-input"
                     [(ngModel)]="editLocation"
                     (blur)="saveValueRate()"
                     [disabled]="savingRates"
                     placeholder="—">
            </label>
            <label class="rate-field">
              <span class="rate-label">{{ 'crm.deadline' | translate }}</span>
              <input nbInput
                     class="rate-input"
                     [placeholder]="'enterdate' | translate"
                     [nbDatepicker]="deadlinePicker"
                     [(ngModel)]="editDeadline"
                     (ngModelChange)="saveValueRate()"
                     [disabled]="savingRates">
              <nb-datepicker #deadlinePicker></nb-datepicker>
            </label>
            <!-- readonly: these two are picker-only. AppModule imports
                 NbDatepickerModule.forRoot() after NbMomentDateModule, so the
                 active NbDateService is NbNativeDateService and typed text goes
                 through Date.parse, which is month-first: a Dutch "12-03-2026"
                 stores 3 December, and "13-03-2026" is rejected outright. The
                 calendar writes through writeValue() and is unaffected. Cost of
                 this: a date can be replaced but not cleared. -->
            <label class="rate-field">
              <span class="rate-label">{{ 'crm.start_date' | translate }}</span>
              <input nbInput readonly
                     class="rate-input"
                     [placeholder]="'enterdate' | translate"
                     [nbDatepicker]="startPicker"
                     [(ngModel)]="editStart"
                     (ngModelChange)="saveValueRate()"
                     [disabled]="savingRates">
              <nb-datepicker #startPicker></nb-datepicker>
            </label>
            <label class="rate-field">
              <span class="rate-label">{{ 'crm.end_date' | translate }}</span>
              <input nbInput readonly
                     class="rate-input"
                     [placeholder]="'enterdate' | translate"
                     [nbDatepicker]="endPicker"
                     [(ngModel)]="editEnd"
                     (ngModelChange)="saveValueRate()"
                     [disabled]="savingRates">
              <nb-datepicker #endPicker></nb-datepicker>
            </label>
          </div>

          <!-- End client: reassignable to any already-known Client of this tenant. -->
          <div class="contact-field">
            <span class="rate-label">{{ 'crm.create.end_client' | translate }}</span>
            <ng-container *ngIf="orgCandidates.length && currentOrgKnown; else orgReadonly">
              <nb-select [(selected)]="selectedOrgId"
                         [placeholder]="'crm.unassigned' | translate"
                         [disabled]="savingOrg"
                         (selectedChange)="changeOrg($event)"
                         size="small"
                         class="contact-select">
                <nb-option *ngFor="let c of orgCandidates" [value]="c.id">
                  {{ c.name }}
                </nb-option>
              </nb-select>
            </ng-container>
            <ng-template #orgReadonly>
              <span class="contact-hint" *ngIf="opp.end_client">{{ opp.end_client?.name }}</span>
              <span class="contact-hint" *ngIf="!opp.end_client">{{ 'crm.unassigned' | translate }}</span>
            </ng-template>
          </div>

          <!-- Owner (verantwoordelijke): reassignable to any staffer/admin. -->
          <div class="contact-field">
            <span class="rate-label">{{ 'crm.col.owner' | translate }}</span>
            <ng-container *ngIf="ownerCandidates.length; else ownerReadonly">
              <nb-select [(selected)]="selectedOwnerId"
                         [placeholder]="'crm.unassigned' | translate"
                         [disabled]="savingOwner"
                         (selectedChange)="changeOwner($event)"
                         size="small"
                         class="contact-select">
                <nb-option *ngFor="let u of ownerCandidates" [value]="u.id">
                  {{ u.name_first }} {{ u.name_last }}
                </nb-option>
              </nb-select>
            </ng-container>
            <ng-template #ownerReadonly>
              <span class="contact-hint" *ngIf="opp.owner">{{ opp.owner.name_first }} {{ opp.owner.name_last }}</span>
              <span class="contact-hint" *ngIf="!opp.owner">{{ 'crm.unassigned' | translate }}</span>
            </ng-template>
          </div>

          <!-- Role: reassignable to any ProjectRole in this tenant's catalogue.
               Always a working select, even with an empty catalogue — the
               ProjectRole directory ships unseeded per tenant, so an empty
               list here is the common case, not a degraded fallback state. -->
          <div class="contact-field">
            <span class="rate-label">{{ 'crm.create.role' | translate }}</span>
            <nb-select [(selected)]="selectedRoleId"
                       [placeholder]="'crm.unassigned' | translate"
                       [disabled]="savingRole"
                       (selectedChange)="changeRole($event)"
                       size="small"
                       class="contact-select">
              <nb-option [value]="null">{{ 'crm.none' | translate }}</nb-option>
              <nb-option *ngFor="let r of roleOptions" [value]="r.id">{{ r.name }}</nb-option>
            </nb-select>
          </div>

          <!-- Skills: structured catalogue skills for this deal. -->
          <div class="contact-field">
            <span class="rate-label">{{ 'crm.create.skills' | translate }}</span>
            <bst-list-adder size="large" [autolist]="true" target="skill"
                             [placeholder]="'search?' | translate"
                             [disabled]="savingSkills"
                             [(ngModel)]="selectedSkills"
                             (ngModelChange)="changeSkills($event)"></bst-list-adder>
          </div>

          <!-- Primary contact picker (staffers only: editable select) -->
          <div class="contact-field">
            <div class="contact-field-head">
              <span class="rate-label">{{ 'crm.contact' | translate }}</span>
              <button nbButton ghost size="tiny" type="button" [disabled]="savingNewContact" (click)="toggleNewContactForm()">
                {{ (showNewContactForm ? 'cancel' : 'crm.new_contact') | translate }}
              </button>
            </div>
            <ng-container *ngIf="contacts.length; else noContacts">
              <nb-select [(selected)]="selectedContactId"
                         [disabled]="savingContact"
                         (selectedChange)="changeContact($event)"
                         size="small"
                         class="contact-select">
                <nb-option [value]="null">{{ 'crm.none' | translate }}</nb-option>
                <nb-option *ngFor="let c of contacts" [value]="c.id">
                  {{ c.name }}{{ c.role ? ' - ' + c.role : '' }}{{ c.company ? ' (' + c.company + ')' : '' }}
                </nb-option>
              </nb-select>
            </ng-container>
            <ng-template #noContacts>
              <span class="contact-hint" *ngIf="!showNewContactForm">{{ 'crm.no_contacts' | translate }}</span>
            </ng-template>
            <div class="contact-card" *ngIf="displayContact && !showNewContactForm">
              <span class="contact-name">{{ displayContact.name }}</span>
              <a *ngIf="displayContact.email" [href]="'mailto:' + displayContact.email" class="contact-email">{{ displayContact.email }}</a>
              <span *ngIf="displayContact.phone" class="contact-meta">{{ displayContact.phone }}</span>
              <span *ngIf="displayContact.role" class="contact-meta">{{ displayContact.role }}</span>
            </div>

            <!-- Inline "new contact" form: creates a Contact for this opportunity's organization and sets it as primary. -->
            <div class="new-contact-form" *ngIf="showNewContactForm">
              <input nbInput fieldSize="small" fullWidth maxlength="255" [(ngModel)]="newContact.name" placeholder="{{ 'contactname' | translate }}">
              <input nbInput fieldSize="small" fullWidth maxlength="255" [(ngModel)]="newContact.role" placeholder="{{ 'contactrole' | translate }}">
              <input nbInput fieldSize="small" fullWidth maxlength="255" [(ngModel)]="newContact.email" placeholder="{{ 'contactemail' | translate }}">
              <input nbInput fieldSize="small" fullWidth maxlength="255" [(ngModel)]="newContact.phone" placeholder="{{ 'contactphone' | translate }}">
              <button nbButton size="small" status="primary" fullWidth
                      [disabled]="!newContact.name?.trim() || savingNewContact"
                      (click)="addContact()">{{ 'addcontact' | translate }}</button>
            </div>
          </div>

          <div class="section-head section-head--sub">
            <h2 class="section-title"><nb-icon icon="people-outline"></nb-icon>{{ 'crm.submissions' | translate }}</h2>
            <button nbButton status="primary" size="tiny" ghost type="button" (click)="openAddSubmission()">
              <nb-icon icon="plus-outline"></nb-icon> {{ 'crm.add_submission' | translate }}
            </button>
          </div>

          <div class="sub-list">
            <div class="sub-row" *ngFor="let s of submissions">
              <nb-user [name]="consultantName(s)"
                       [picture]="Globals.logo(s.consultant)"
                       class="sub-user-link"
                       (click)="openSubmission(s)">
              </nb-user>
              <span class="status-badge" [class]="'sub-is-' + s.status">{{ ('crm.sub.' + s.status) | translate }}</span>
            </div>

            <!-- Empty state: opportunity loaded but no submissions yet. -->
            <div class="empty-state-compact" *ngIf="!isLoading && !submissions.length">
              <nb-icon class="empty-icon" icon="people-outline"></nb-icon>
              <p class="empty-title">{{ 'crm.no_submissions' | translate }}</p>
            </div>
          </div>

          <!-- Quotes (#1697): only with Quotes on for the company and Admin or the Quotes grant. -->
          <ng-container *ngIf="canQuote">
            <div class="section-head section-head--sub">
              <h2 class="section-title">{{ 'quotes.title' | translate }}</h2>
              <button nbButton status="primary" size="tiny" ghost type="button" [disabled]="creatingQuote" (click)="newQuote()">
                <nb-icon icon="plus-outline"></nb-icon> {{ 'quotes.new' | translate }}
              </button>
            </div>
            <div class="quote-list">
              <div class="quote-row" *ngFor="let q of quotes">
                <a class="quote-link" [routerLink]="['/quotes', q.id]" (click)="$event.preventDefault(); openQuote(q.id)">
                  <span class="quote-number">{{ q.number }}</span>
                  <span class="quote-date">{{ q.quote_date | localedate:'D MMM YYYY' }}</span>
                </a>
                <button nbButton ghost size="tiny" type="button" [attr.aria-label]="('quotes.download' | translate) + ' ' + q.number"
                        [disabled]="!q.printable" [title]="q.printable ? '' : ('quotes.pdf_open_first' | translate)" (click)="downloadQuote(q.id)">
                  <nb-icon icon="download-outline"></nb-icon>
                </button>
                <button nbButton ghost size="tiny" status="danger" type="button" [attr.aria-label]="('quotes.delete' | translate) + ' ' + q.number" (click)="deleteQuote(q)">
                  <nb-icon icon="trash-2-outline"></nb-icon>
                </button>
              </div>
              <div class="empty-state-compact" *ngIf="!isLoading && !quotes.length">
                <p class="empty-title">{{ 'quotes.none_yet' | translate }}</p>
              </div>
            </div>
          </ng-container>

          <!-- Action buttons: reopen / archive / unarchive / delete -->
          <div class="action-buttons" *ngIf="opp.status === 'won' || opp.status === 'archived' || canDelete">
            <button nbButton size="small" status="primary" fullWidth
                    *ngIf="opp.status === 'won' && !opp.converted_Project_id"
                    (click)="reopen()">{{ 'crm.reopen' | translate }}</button>
            <button nbButton size="small" status="danger" ghost fullWidth *ngIf="canDelete"
                    (click)="deleteOpp()">{{ 'crm.delete' | translate }}</button>
          </div>
        </section>
      </aside>

      <!-- Read-only contact card for non-staffers (when primary contact is set) -->
      <aside class="detail-rail" *ngIf="!canCreate && displayContact">
        <section class="rail-block">
          <span class="rate-label">{{ 'crm.contact' | translate }}</span>
          <div class="contact-card">
            <span class="contact-name">{{ displayContact.name }}</span>
            <a *ngIf="displayContact.email" [href]="'mailto:' + displayContact.email" class="contact-email">{{ displayContact.email }}</a>
            <span *ngIf="displayContact.phone" class="contact-meta">{{ displayContact.phone }}</span>
            <span *ngIf="displayContact.role" class="contact-meta">{{ displayContact.role }}</span>
          </div>
        </section>
      </aside>

      <!-- Main: activity composer + timeline (the thing that accumulates) -->
      <div class="detail-main">
        <div class="section-head">
          <h2 class="section-title"><nb-icon icon="activity-outline"></nb-icon>{{ 'crm.activity' | translate }}</h2>
        </div>

        <bst-crm-activity-log *ngIf="canCreate" [saving]="savingActivity" [contacts]="contacts" (add)="addActivity($event)"></bst-crm-activity-log>

        <!-- Activity timeline (newest-first from API) -->
        <bst-crm-timeline *ngIf="activities.length" [activities]="activities" [canEdit]="canCreate"
                           (edit)="editActivity($event)" (delete)="deleteActivity($event)"></bst-crm-timeline>
        <div class="crm-empty" *ngIf="!isLoading && !activities.length">{{ 'crm.no_activity' | translate }}</div>
      </div>

    </div>

  </ng-container>
</div>
